/**
 * importengine domain — imperative API functions + domain types.
 *
 * These are thin wrappers over the Kubb-generated client functions,
 * preserving the exact signatures and rich return types the import
 * wizard relies on. They stay hand-written (not generated hooks) because
 * every consumer calls them imperatively — from the XState wizard actors
 * (`fetchImportPreviewData` is the orchestrator, not part of the query
 * surface) and the contact-import step handlers.
 *
 * The generated response types (`ImportPreview`, `ImportPreviewNoData`)
 * describe only the model-serializer fields — drf-spectacular can't see
 * the JSON `data` payload's real shape, nor the `raw_data` / `type` /
 * `from_unmatched_chains` fields the frontend depends on. So we cast the
 * generated responses to the canonical `ImportPreviewDB` / `ImportPreviewNoData`
 * models from `importer-types` (the single source of truth for those shapes).
 */

import type {
  ImportDataRowsFormat,
  ImportPreviewDB,
  ImportPreviewNoData,
} from 'features/importerv2/importer-types'

import { createImportPreview as createImportPreviewClient } from '../generated/clients/createImportPreview'
import { getImportPreview as getImportPreviewClient } from '../generated/clients/getImportPreview'
import { getImportPreviewStatus as getImportPreviewStatusClient } from '../generated/clients/getImportPreviewStatus'
import { getUnmatchedChainsPreview as getUnmatchedChainsPreviewClient } from '../generated/clients/getUnmatchedChainsPreview'
import { mergeUnmatchedChains as mergeUnmatchedChainsClient } from '../generated/clients/mergeUnmatchedChains'
import { performSetCompanies as performSetCompaniesClient } from '../generated/clients/performSetCompanies'
import { performSetCompanyNotes as performSetCompanyNotesClient } from '../generated/clients/performSetCompanyNotes'
import { performSetContacts as performSetContactsClient } from '../generated/clients/performSetContacts'
import { performSetDeals as performSetDealsClient } from '../generated/clients/performSetDeals'
import { performSetGpos as performSetGposClient } from '../generated/clients/performSetGpos'
import { performSetTags as performSetTagsClient } from '../generated/clients/performSetTags'
import { rePrepareImportPreview as rePrepareImportPreviewClient } from '../generated/clients/rePrepareImportPreview'
import { updateImportPreview as updateImportPreviewClient } from '../generated/clients/updateImportPreview'
import { validateDealAccountOwners as validateDealAccountOwnersClient } from '../generated/clients/validateDealAccountOwners'
import { validateDealProducts as validateDealProductsClient } from '../generated/clients/validateDealProducts'
import { validateDealProductVariants as validateDealProductVariantsClient } from '../generated/clients/validateDealProductVariants'
import { validateDealSalesStages as validateDealSalesStagesClient } from '../generated/clients/validateDealSalesStages'

import type { CreateImportPreviewMutationRequest } from '../generated/types/CreateImportPreview'
import type { PerformSetContactsMutationRequest } from '../generated/types/PerformSetContacts'
import type { RePrepareImportPreviewMutationRequest } from '../generated/types/RePrepareImportPreview'
import type { UpdateImportPreviewMutationRequest } from '../generated/types/UpdateImportPreview'

// ── Request/response types (mirror the legacy importer-api surface) ──

export type CreateImportPreviewParams = {
  import_data: {
    data: ImportDataRowsFormat
    raw_data?: string
    name?: string
    skip_matching?: boolean
  }
}

export type UpdateImportPreviewParams = CreateImportPreviewParams

export interface ImportCustomColumn {
  header: string
  name: string
}

export interface ValidateDealAccountOwnersResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export interface ValidateDealSalesStagesResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export interface ValidateDealProductsResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export interface MissingVariant {
  variant: string
  product_name?: string
  product_id?: number
}

export interface ValidateDealProductVariantsResponse {
  success: boolean
  missing: MissingVariant[]
  found: string[]
}

// ── Import preview CRUD ─────────────────────────────────────────────

export async function createImportPreview(
  values: CreateImportPreviewParams
): Promise<ImportPreviewDB> {
  return (await createImportPreviewClient(
    values as unknown as CreateImportPreviewMutationRequest,
    { successMessage: 'Import preview created successfully' }
  )) as unknown as ImportPreviewDB
}

// Standalone function for fetching preview (used by the XState machine).
export async function fetchImportPreviewData(
  importNumber: number
): Promise<ImportPreviewDB> {
  return (await getImportPreviewClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewDB
}

export async function updateImportPreview(
  importNumber: number,
  values: Partial<UpdateImportPreviewParams>
): Promise<number> {
  return updateImportPreviewClient(
    { import_number: importNumber },
    values as unknown as UpdateImportPreviewMutationRequest
  )
}

/**
 * Re-run the prepare task on an EXISTING preview, in place. Reuses the same
 * import_number (no orphaned preview) and replaces the stored rows with the
 * re-mapped data so prepare recomputes the internal columns for the new
 * mapping. Used by MapColumnsStep when the user navigates Back then Next with
 * a changed column mapping.
 */
export async function rePrepareImportPreview(
  importNumber: number,
  importData: ImportDataRowsFormat
): Promise<ImportPreviewNoData> {
  return (await rePrepareImportPreviewClient({ import_number: importNumber }, {
    import_data: { data: importData },
  } as unknown as RePrepareImportPreviewMutationRequest)) as unknown as ImportPreviewNoData
}

// ── Deal validation ─────────────────────────────────────────────────

export async function validateDealAccountOwners(
  importNumber: number
): Promise<ValidateDealAccountOwnersResponse> {
  return (await validateDealAccountOwnersClient({
    import_number: importNumber,
  })) as unknown as ValidateDealAccountOwnersResponse
}

export async function validateDealSalesStages(
  importNumber: number
): Promise<ValidateDealSalesStagesResponse> {
  return (await validateDealSalesStagesClient({
    import_number: importNumber,
  })) as unknown as ValidateDealSalesStagesResponse
}

export async function validateDealProducts(
  importNumber: number
): Promise<ValidateDealProductsResponse> {
  return (await validateDealProductsClient({
    import_number: importNumber,
  })) as unknown as ValidateDealProductsResponse
}

export async function validateDealProductVariants(
  importNumber: number
): Promise<ValidateDealProductVariantsResponse> {
  return (await validateDealProductVariantsClient({
    import_number: importNumber,
  })) as unknown as ValidateDealProductVariantsResponse
}

// ── State-machine steps ─────────────────────────────────────────────

export async function performSetCompanies(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await performSetCompaniesClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

export async function performSetTags(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await performSetTagsClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

export async function performSetGpos(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await performSetGposClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

export async function performSetDeals(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await performSetDealsClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

export async function performSetContacts(
  importNumber: number,
  existingContactMode: 'keep' | 'update' = 'keep',
  campaignId?: number | null,
  customColumns?: ImportCustomColumn[]
): Promise<ImportPreviewNoData> {
  return (await performSetContactsClient({ import_number: importNumber }, {
    existing_contact_mode: existingContactMode,
    ...(campaignId != null ? { campaign_id: campaignId } : {}),
    ...(customColumns && customColumns.length > 0
      ? { custom_columns: customColumns }
      : {}),
  } as unknown as PerformSetContactsMutationRequest)) as unknown as ImportPreviewNoData
}

export async function performSetCompanyNotes(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await performSetCompanyNotesClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

// ── Status polling ──────────────────────────────────────────────────

// Standalone function for fetching status (used by the XState machine).
export async function fetchImportPreviewStatus(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await getImportPreviewStatusClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}

// ── Unmatched chains ────────────────────────────────────────────────

export async function getUnmatchedChainsPreview(): Promise<{
  import_number: number
}> {
  return getUnmatchedChainsPreviewClient()
}

export async function setUnmatchedChains(
  importNumber: number
): Promise<ImportPreviewNoData> {
  return (await mergeUnmatchedChainsClient({
    import_number: importNumber,
  })) as unknown as ImportPreviewNoData
}
